// frontend/src/App.jsx
import { Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";

import Menu from "./pages/Menu";
import SizeCheck from "./pages/SizeCheck";
import EWP from "./pages/EWP";
import ProductInfo from "./pages/ProductInfo";
import FabricOriginSearch from "./pages/FabricOriginSearch";
import EstimatePacking from "./pages/EstimatePacking";
import EwpAccessory from "./pages/EwpAccessory";
import PurchaseConfig from "./pages/PurchaseConfig";
import HSCode from "./pages/HSCode";
import SRPConfig from "./pages/SRPConfig";
import RetailAccessory from "./pages/RetailAccessory";
import Staff from "./pages/Staff";
import Login from "./Login.jsx";
import QuickCheck from "./pages/QuickCheck";
import SA_5ngonngu from "./pages/SA_5ngonngu";
import KA_5ngonngu from "./pages/KA_5ngonngu";
import SA_DiSC from "./pages/SA_DiSC";
import KA_DiSC from "./pages/KA_DiSC";
import SA_RiceBowl from "./pages/SA_RiceBowl";
import KA_RiceBowl from "./pages/KA_RiceBowl";
import EosTraining from "./pages/EosTraining";
import BomManagement from "./pages/BomManagement";
import HdPurchase from "./pages/HdPurchase";
import HdFinishedGoodsPurchase from "./pages/HdFinishedGoodsPurchase";

// EOS Training Admin
import EosAdmin from "./pages/EosAdmin";

// Web Email (temp) page
import WebEmail from "./pages/WebEmail";

// SRP Courier page (width-based fee management)
import SRPCourier from "./pages/SRPCourier";
import ShippingCrates from "./pages/ShippingCrates";

// NEW: System Admin (blank)
import SystemAdmin from "./pages/SystemAdmin";
import SystemAdminAccess from "./pages/SystemAdminAccess";

/**
 * Route guard for Purchase area:
 * - Checks ewp_auth via /api/auth?action=me (30-day cookie set by EWP_PASSWORD)
 * - Preserves layout by simply redirecting to /menu when unauthorized
 */
function PurchaseGate({ children }) {
  const [ready, setReady] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch("/api/auth?action=me", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        if (!mounted) return;
        setOk(!!(j && j.ok === true));
      } catch {
        if (!mounted) return;
        setOk(false);
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!ready) return null; // no spinner to keep UI/layout untouched
  if (!ok) return <Navigate to="/menu" replace />;
  return children;
}

/**
 * Route guard for Admin area:
 * - Checks /api/auth?action=me-user
 * - Only lets you in if j.admin === true
 */
function AdminGate({ children }) {
  const [ready, setReady] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await fetch("/api/auth?action=me-user", { credentials: "include" });
        const j = await r.json().catch(() => ({}));
        if (!mounted) return;
        // The magic check: Are you logged in AND an admin?
        setOk(!!(j && j.ok === true && j.admin === true));
      } catch {
        if (!mounted) return;
        setOk(false);
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  if (!ready) return null;
  if (!ok) return <Navigate to="/menu" replace />;
  return children;
}

export default function App() {
  // Router is provided by index.js; don't wrap again here
  return (
    <div className="app-shell">
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/menu" element={<Menu />} />
        <Route path="/dept/:deptKey" element={<Menu />} />
        <Route path="/size-check" element={<SizeCheck />} />
        <Route path="/quick-check" element={<QuickCheck />} />
        <Route path="/product-info" element={<ProductInfo />} />
        <Route path="/fabric-origin" element={<FabricOriginSearch />} />

        {/* NEW PUBLIC ROUTE FOR THE STAFF QUIZ */}
        <Route path="/ngon-ngu-suong" element={<SA_5ngonngu />} />
        <Route path="/kiem-tra-tinh-te" element={<KA_5ngonngu />} />

        {/* NEW PUBLIC ROUTES FOR DiSC QUIZ */}
        <Route path="/kham-pha-disc" element={<SA_DiSC />} />
        <Route path="/kiem-tra-disc" element={<KA_DiSC />} />
        <Route path="/kham-pha-to-com" element={<SA_RiceBowl />} />
        <Route path="/kiem-tra-to-com" element={<KA_RiceBowl />} />
        <Route path="/eos" element={<EosTraining />} />

        {/* Purchase (gated by ewp_auth) */}
        <Route
          path="/purchase/ewp"
          element={
            <PurchaseGate>
              <EWP />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/packing-estimate"
          element={
            <PurchaseGate>
              <EstimatePacking />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/ewp-accessory"
          element={
            <PurchaseGate>
              <EwpAccessory />
            </PurchaseGate>
          }
        />

        {/* Purchase Configuration (gated) */}
        <Route
          path="/purchase/config"
          element={
            <PurchaseGate>
              <PurchaseConfig />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/hs-code"
          element={
            <PurchaseGate>
              <HSCode />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/srp"
          element={
            <PurchaseGate>
              <SRPConfig />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/retail-product"
          element={
            <PurchaseGate>
              <RetailAccessory />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/srp-courier"
          element={
            <PurchaseGate>
              <SRPCourier />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/shipping-crates"
          element={
            <PurchaseGate>
              <ShippingCrates />
            </PurchaseGate>
          }
        />
        <Route
          path="/purchase/config/staff"
          element={
            <PurchaseGate>
              <Staff />
            </PurchaseGate>
          }
        />

        {/* NEW: System Admin (blank landing) */}        <Route
          path="/purchase/bom"
          element={
            <PurchaseGate>
              <BomManagement />
            </PurchaseGate>
          }
        />
        <Route
  path="/purchase/order-management"
  element={
    <PurchaseGate>
      <HdPurchase />
    </PurchaseGate>
  }
/>
        <Route
          path="/purchase/finished-goods-order"
          element={
            <PurchaseGate>
              <HdFinishedGoodsPurchase />
            </PurchaseGate>
          }
        />
        {/* NEW: System Admin (blank landing) */}
        <Route path="/sysadmin" element={<SystemAdmin />} />
        <Route path="/sysadmin/access" element={<SystemAdminAccess />} />
        <Route 
          path="/sysadmin/staff" 
          element={
            <AdminGate>
              <Staff />
            </AdminGate>
          } 
        />

        {/* ---> PASTE THIS NEW ROUTE RIGHT HERE <--- */}
        <Route 
          path="/sysadmin/eos-setup" 
          element={
            <AdminGate>
              <EosAdmin />
            </AdminGate>
          } 
        />
        {/* ---------------------------------------- */}

        {/* NEW: Web Email (temp) under Purchase (gated) */}
        <Route
          path="/purchase/web-email"
          element={
            <PurchaseGate>
              <WebEmail />
            </PurchaseGate>
          }
        />

        <Route path="/login" element={<Login />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
